import React, { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Helmet } from "react-helmet"
import NavigationBar from "../../components/ui/NavigationBar"
import Input from "../../components/ui/Input"
import Select from "../../components/ui/Select"
import Button from "../../components/ui/Button"
import Icon from "../../components/AppIcon"
import { Badge } from "../../components/ui/badge"
import ComingSoonPanel from "../organization-dashboard/components/ComingSoonPanel"
import CohortLearnersTab from "./components/CohortLearnersTab"
import CohortScheduleTab from "./components/CohortScheduleTab"
import CohortAnnouncementsTab from "./components/CohortAnnouncementsTab"
import CohortAttendanceTab from "./components/CohortAttendanceTab"
import { getCohort, updateCohort, computeCohortSummary } from "../../utils/cohortHelpers"
import CohortAssessmentsTab from "./components/CohortAssessmentsTab"
import CohortCredentialsTab from "./components/CohortCredentialsTab"
import CohortAnalyticsTab from "./components/CohortAnalyticsTab"
import { getCourse } from "../../utils/courseHelpers"

const TABS = [
  { key: "overview", label: "Overview", icon: "LayoutDashboard" },
  { key: "learners", label: "Learners", icon: "Users" },
  { key: "schedule", label: "Schedule", icon: "Calendar" },
  { key: "curriculum", label: "Curriculum", icon: "BookOpen" },
  { key: "attendance", label: "Attendance", icon: "ClipboardCheck" },
  { key: "assessments", label: "Assessments", icon: "FileCheck" },
  { key: "projects", label: "Projects", icon: "FolderKanban" },
  { key: "announcements", label: "Announcements", icon: "Megaphone" },
  { key: "credentials", label: "Credentials", icon: "Award" },
  { key: "analytics", label: "Analytics", icon: "BarChart3" },
]

// Mirrors organization-program-builder/index.jsx's structure. Unlike ProgramBuilder,
// cohortHelpers.js has a direct single-fetch getCohort(orgId, cohortId) â€” no
// listCohorts + find workaround needed here.
const OrganizationCohortWorkspace = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const orgId = searchParams?.get("orgId")
  const cohortId = searchParams?.get("cohortId")

  const [activeTab, setActiveTab] = useState("overview")
  const [cohort, setCohort] = useState(null)
  const [course, setCourse] = useState(null)
  const [summary, setSummary] = useState({ total: 0, activePercent: 0, completionPercent: 0 })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveMessage, setSaveMessage] = useState("")

  const loadCohort = async () => {
    if (!orgId || !cohortId) {
      navigate("/organization-dashboard", { replace: true })
      return
    }
    const { cohort: found } = await getCohort(orgId, cohortId)
    if (!found) {
      navigate("/organization-dashboard", { replace: true })
      return
    }
    setCohort(found)

    const [{ course: courseData }, summaryData] = await Promise.all([
      getCourse(orgId, found?.courseId),
      computeCohortSummary(orgId, found),
    ])
    setCourse(courseData || null)
    setSummary(summaryData)
    setLoading(false)
  }

    useEffect(() => {
    loadCohort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, cohortId])

  // Lightweight re-fetch for after a Schedule/Announcements mutation — refreshes
  // just the cohort doc (where scheduleEvents/announcements live) without
  // re-triggering loadCohort's full-page loading spinner.
  const refreshCohort = async () => {
    const { cohort: found } = await getCohort(orgId, cohortId)
    if (found) setCohort(found)
  }

  const handleFieldChange = (field) => (e) => {
    setCohort((prev) => ({ ...prev, [field]: e?.target?.value }))
  }

  const handleSaveOverview = async () => {
    setSaving(true)
    setSaveMessage("")
    const { error } = await updateCohort(orgId, cohortId, {
      name: cohort?.name,
      description: cohort?.description,
      status: cohort?.status,
    })
    setSaving(false)
    if (error) {
      setSaveMessage("Something went wrong saving. Please try again.")
      return
    }
    setSaveMessage("Saved successfully.")
    setTimeout(() => setSaveMessage(""), 3000)
  }

  // Upcoming = today or later, soonest first. Past events are just left out of
  // this preview list (still visible in full on the Schedule tab once built).
  const upcomingEvents = (cohort?.scheduleEvents || [])
    ?.filter((ev) => !ev?.date || new Date(ev.date) >= new Date(new Date().toDateString()))
    ?.sort((a, b) => new Date(a?.date || 0) - new Date(b?.date || 0))
    ?.slice(0, 3)

  const recentAnnouncements = [...(cohort?.announcements || [])]
    ?.sort((a, b) => new Date(b?.postedAt || 0) - new Date(a?.postedAt || 0))
    ?.slice(0, 3)

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading cohort...</span>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{cohort?.name || "Cohort Workspace"} - Nesolearn</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate("/organization-dashboard")}>
              Back to Cohorts
            </Button>
            {saveMessage && (
              <span className="text-sm text-success flex items-center gap-1">
                <Icon name="CheckCircle2" size={14} />
                {saveMessage}
              </span>
            )}
          </div>

          <div className="mb-6 flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">{cohort?.name || "Untitled Cohort"}</h1>
            <Badge variant={cohort?.status === "archived" ? "warning" : "success"}>
              {cohort?.status === "archived" ? "Archived" : "Active"}
            </Badge>
          </div>

          <div className="border-b border-border mb-6 overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {TABS?.map((tab) => (
                <button
                  key={tab?.key}
                  type="button"
                  onClick={() => setActiveTab(tab?.key)}
                  className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab?.key ? "border-accent text-accent" : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon name={tab?.icon} size={14} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "overview" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <p className="text-xs text-muted-foreground">Learners</p>
                  <p className="text-2xl font-bold text-foreground">{summary.total}</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <p className="text-xs text-muted-foreground">Active</p>
                  <p className="text-2xl font-bold text-foreground">{summary.activePercent}%</p>
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <p className="text-xs text-muted-foreground">Completion</p>
                  <p className="text-2xl font-bold text-foreground">{summary.completionPercent}%</p>
                </div>
              </div>

              <div className="bg-card border border-border rounded-xl p-6 space-y-4">
                <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">Cohort Details</h2>
                <Input label="Name" value={cohort?.name} onChange={handleFieldChange("name")} />
                <div>
                  <label className="text-sm font-medium text-foreground mb-2 block">Description</label>
                  <textarea
                    rows={3}
                    value={cohort?.description}
                    onChange={handleFieldChange("description")}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Course</label>
                    <p className="text-sm text-foreground px-3 py-2 bg-muted rounded-md">{course?.title || "Unknown course"}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground mb-2 block">Status</label>
                    <Select
                      options={[{ value: "active", label: "Active" }, { value: "archived", label: "Archived" }]}
                      value={cohort?.status || "active"}
                      onChange={(v) => setCohort((c) => ({ ...c, status: v }))}
                    />
                  </div>
                </div>
                <div className="pt-2">
                  <Button variant="default" loading={saving} onClick={handleSaveOverview}>
                    Save Changes
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Upcoming Schedule</h3>
                  {upcomingEvents?.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No upcoming events.</p>
                  ) : (
                    <ul className="space-y-2">
                      {upcomingEvents?.map((ev) => (
                        <li key={ev?.id} className="text-xs text-foreground">
                          <span className="font-medium">{ev?.title}</span>{" "}
                            <span className="text-muted-foreground">{ev?.date}{ev?.time ? ` - ${ev.time}` : ""}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="bg-card border border-border rounded-xl p-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Recent Announcements</h3>
                  {recentAnnouncements?.length === 0 ? (
                    <p className="text-xs text-muted-foreground">No announcements yet.</p>
                  ) : (
                    <ul className="space-y-2">
                      {recentAnnouncements?.map((a) => (
                        <li key={a?.id} className="text-xs text-foreground">
                          <span className="font-medium">{a?.title}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "learners" && (
            <CohortLearnersTab orgId={orgId} cohortId={cohortId} courseId={cohort?.courseId} cohortName={cohort?.name} />
          )}

                    {activeTab === "schedule" && (
            <CohortScheduleTab orgId={orgId} cohortId={cohortId} events={cohort?.scheduleEvents || []} onChange={refreshCohort} />
          )}

          {activeTab === "curriculum" && (
            <div className="bg-card border border-border rounded-xl p-6 space-y-4">
              <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">
                Curriculum <span className="text-muted-foreground font-normal normal-case">(read-only â€” edit in the course builder)</span>
              </h2>
              {(course?.modules || [])?.length === 0 ? (
                <p className="text-sm text-muted-foreground">This course has no modules yet.</p>
              ) : (
                <div className="space-y-3">
                  {course?.modules?.map((mod, i) => (
                    <div key={mod?.id || i} className="border border-border rounded-lg p-4">
                      <p className="text-sm font-medium text-foreground mb-2">{mod?.title || `Module ${i + 1}`}</p>
                      <ul className="space-y-1">
                        {mod?.lessons?.map((lesson, j) => (
                          <li key={lesson?.id || j} className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Icon name="Circle" size={6} />
                            {lesson?.title || `Lesson ${j + 1}`}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

                    {activeTab === "attendance" && (
            <CohortAttendanceTab
              orgId={orgId}
              cohortId={cohortId}
              courseId={cohort?.courseId}
              scheduleEvents={cohort?.scheduleEvents || []}
              attendance={cohort?.attendance || {}}
              onChange={refreshCohort}
            />
          )}
                    {activeTab === "assessments" && <CohortAssessmentsTab orgId={orgId} cohort={cohort} />}
          {activeTab === "projects" && <ComingSoonPanel label="Projects" />}
                    {activeTab === "announcements" && (
            <CohortAnnouncementsTab orgId={orgId} cohortId={cohortId} announcements={cohort?.announcements || []} onChange={refreshCohort} />
          )}
                    {activeTab === "credentials" && <CohortCredentialsTab orgId={orgId} cohort={cohort} />}
          {activeTab === "analytics" && (
            <CohortAnalyticsTab orgId={orgId} cohort={cohort} summary={summary} />
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
            <Button variant="outline" iconName="ArrowLeft" iconPosition="left" onClick={() => navigate("/organization-dashboard")}>
              Back to Cohorts
            </Button>
          </div>
        </main>
      </div>
    </>
  )
}

export default OrganizationCohortWorkspace